import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { Edit, Trash2, Download, Plus, X, Pill, Search, AlertTriangle, Users, CheckCircle, XCircle, PauseCircle, ChevronLeft, ChevronRight } from 'lucide-react'; 
import axios from 'axios';

const Patients = () => {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // 1. INITIALIZE STATE
  const [searchTerm, setSearchTerm] = useState(() => sessionStorage.getItem('patients_search') || '');
  const [entriesPerPage, setEntriesPerPage] = useState(() => parseInt(sessionStorage.getItem('patients_entries')) || 10);
  const [currentPage, setCurrentPage] = useState(() => parseInt(sessionStorage.getItem('patients_page')) || 1);

  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [patientToDelete, setPatientToDelete] = useState(null); 

  const [formData, setFormData] = useState({
    full_name: '', phone_number: '', dob: '', address: '', 
    insurance_provider: '', insurance_policy_no: '', status: 'Active'
  });

  // ✅ LOCALHOST URL
  const API_URL = 'https://unifyrxcrm.com/AG-BACKEND/index.php';

  useEffect(() => {
    fetchPatients();
  }, []);

  useEffect(() => {
    sessionStorage.setItem('patients_search', searchTerm);
    sessionStorage.setItem('patients_entries', entriesPerPage);
    sessionStorage.setItem('patients_page', currentPage);
  }, [searchTerm, entriesPerPage, currentPage]);

  const fetchPatients = async () => {
    try {
      const response = await axios.get(`${API_URL}?action=get_patients&_t=${Date.now()}`);
      if (Array.isArray(response.data)) {
        setPatients(response.data);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching patients:", error);
      setLoading(false);
    }
  };

  // ✅ NEW: Status Badge Helper (Clean & Consistent)
  const getPatientStatusBadge = (status) => {
    const s = (status || 'Active').toLowerCase();
    
    if (s === 'active') {
        return { 
            className: 'bg-green-100 text-green-700 border border-green-200', 
            icon: <CheckCircle size={12}/> 
        };
    }
    if (s === 'inactive') {
        return { 
            className: 'bg-gray-100 text-gray-600 border border-gray-200', 
            icon: <XCircle size={12}/> 
        };
    }
    if (s === 'hold' || s === 'deceased') {
        return { 
            className: 'bg-orange-100 text-orange-700 border border-orange-200', 
            icon: <PauseCircle size={12}/> 
        };
    }
    // Default
    return { 
        className: 'bg-gray-100 text-gray-600 border border-gray-200', 
        icon: <XCircle size={12}/> 
    };
  };

  const formatID = (id) => String(id).padStart(4, '0'); 
  
  const formatDate = (dateString) => {
    if (!dateString || dateString === '0000-00-00') return '';
    const cleanDate = dateString.split(' ')[0].split('T')[0]; 
    const [year, month, day] = cleanDate.split('-');
    return (year && month && day) ? `${month}/${day}/${year}` : dateString;
  };

  const filteredPatients = patients.filter((patient) => {
    const search = searchTerm.toLowerCase();
    const name = (patient.full_name || '').toLowerCase();
    const id = String(patient.id);
    return (name.includes(search) || id.includes(search) || (patient.phone_number || '').includes(search));
  });

  const totalEntries = filteredPatients.length;
  const totalPages = entriesPerPage === 'all' ? 1 : Math.ceil(totalEntries / entriesPerPage);
  const startIndex = entriesPerPage === 'all' ? 0 : (currentPage - 1) * entriesPerPage;
  const endIndex = entriesPerPage === 'all' ? totalEntries : startIndex + entriesPerPage;
  const paginatedPatients = filteredPatients.slice(startIndex, endIndex);

  useEffect(() => {
      if (currentPage > totalPages && totalPages > 0) setCurrentPage(1);
  }, [searchTerm, totalPages]);

  const handleInputChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleSearchChange = (e) => { setSearchTerm(e.target.value); setCurrentPage(1); };
  
  const handlePatientClick = (id) => {
      sessionStorage.setItem('lastViewedPatientId', id);
      navigate(`/patient/${id}`);
  };

  const openAddModal = () => {
    setIsEditing(false); setEditId(null);
    setFormData({ full_name: '', phone_number: '', dob: '', address: '', insurance_provider: '', insurance_policy_no: '', status: 'Active' });
    setShowModal(true);
  };

  const handleEditClick = (e, patient) => {
    e.stopPropagation(); setIsEditing(true); setEditId(patient.id);
    let cleanDob = patient.dob ? patient.dob.split(' ')[0] : '';
    setFormData({
        full_name: patient.full_name, phone_number: patient.phone_number, dob: cleanDob,
        address: patient.address || '', insurance_provider: patient.insurance_provider,
        insurance_policy_no: patient.insurance_policy_no, status: patient.status || 'Active'
    });
    setShowModal(true);
  };

  const confirmDeleteClick = (e, patient) => { e.stopPropagation(); setPatientToDelete(patient); setShowDeleteModal(true); };

  const executeDelete = async () => {
    if (!patientToDelete) return;
    try {
        await axios.post(`${API_URL}?action=delete_patient`, { id: patientToDelete.id });
        setShowDeleteModal(false); setPatientToDelete(null); fetchPatients();
    } catch (error) { console.error(error); alert("Error deleting patient"); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const action = isEditing ? 'update_patient' : 'add_patient';
    const payload = isEditing ? { ...formData, id: editId } : formData;
    try {
        const res = await axios.post(`${API_URL}?action=${action}`, payload);
        if (res.data.success) {
            alert(isEditing ? "Patient Updated!" : "Patient Registered!");
            setShowModal(false); fetchPatients();
        } else { alert("Error saving patient"); }
    } catch (error) { console.error(error); alert("System Error"); }
  };

  const handleDownloadExcel = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(patients);
    XLSX.utils.book_append_sheet(wb, ws, "Patients");
    XLSX.writeFile(wb, "AG_Pharmacy_Patients.xlsx");
  };

  return (
    <div className="relative">
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
        <h2 className="text-2xl font-bold text-gray-700 flex items-center gap-3">
            <Users className="text-teal-600" size={30} />
            Patients List
        </h2>
        <div className="flex flex-col sm:flex-row gap-4 items-center w-full md:w-auto">
            <div className="relative w-full sm:w-auto">
                <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
                <input type="text" placeholder="Search Name or ID..." className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 w-64" value={searchTerm} onChange={handleSearchChange} />
            </div>
            <button onClick={openAddModal} className="bg-teal-600 text-white px-4 py-2 rounded hover:bg-teal-700 flex items-center gap-2 transition shadow-md"><Plus size={16} /> Register</button>
            <button onClick={handleDownloadExcel} className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 flex items-center gap-2 transition shadow-md"><Download size={16} /> Excel</button>
        </div>
      </div>

      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Show</span>
          <select value={entriesPerPage} onChange={(e) => { setEntriesPerPage(e.target.value === 'all' ? 'all' : parseInt(e.target.value)); setCurrentPage(1); }} className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500">
            <option value={10}>10</option><option value={25}>25</option><option value={50}>50</option><option value={100}>100</option><option value="all">All</option>
          </select>
          <span className="text-sm text-gray-600">entries</span>
        </div>
        <div className="text-sm text-gray-600">Showing {startIndex + 1} to {Math.min(endIndex, totalEntries)} of {totalEntries} entries</div>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-hidden border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left font-bold text-gray-500 uppercase">ID</th>
              <th className="px-6 py-3 text-left font-bold text-gray-500 uppercase">Patient Name</th>
              <th className="px-6 py-3 text-left font-bold text-gray-500 uppercase">Address</th>
              <th className="px-6 py-3 text-left font-bold text-gray-500 uppercase">Phone Number</th>
              <th className="px-6 py-3 text-left font-bold text-gray-500 uppercase">DOB</th>
              <th className="px-6 py-3 text-left font-bold text-gray-500 uppercase">Insurance</th>
              <th className="px-6 py-3 text-center font-bold text-gray-500 uppercase">RX Count</th>
              <th className="px-6 py-3 text-center font-bold text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left font-bold text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? ( <tr><td colSpan="9" className="text-center py-4">Loading...</td></tr> ) : paginatedPatients.length === 0 ? ( <tr><td colSpan="9" className="text-center py-10 text-gray-400 italic">No patients found.</td></tr> ) : (
                paginatedPatients.map((patient) => {
                    // Get Badge Styles
                    const badge = getPatientStatusBadge(patient.status);
                    
                    return (
                        <tr key={patient.id} id={`patient-row-${patient.id}`} className="hover:bg-teal-50 transition duration-150 cursor-pointer" onClick={() => handlePatientClick(patient.id)}>
                            <td className="px-6 py-4 whitespace-nowrap text-gray-500 font-mono">{formatID(patient.id)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-teal-600 font-bold hover:underline">{patient.full_name}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-gray-700 max-w-xs truncate" title={patient.address}>{patient.address || '-'}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-gray-700">{patient.phone_number}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-gray-700">{formatDate(patient.dob)}</td>
                            <td className="px-6 py-4 whitespace-nowrap"><span className="block text-gray-900 font-medium">{patient.insurance_provider}</span></td>
                            <td className="px-6 py-4 whitespace-nowrap text-center"><span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${patient.rx_count > 0 ? 'bg-teal-100 text-teal-800' : 'bg-gray-100 text-gray-800'}`}><Pill size={12} className="mr-1"/> {patient.rx_count}</span></td>
                            
                            {/* ✅ FIXED STATUS BADGE */}
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                                <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold ${badge.className}`}>
                                    {badge.icon} {patient.status || 'Active'}
                                </span>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap flex gap-3" onClick={(e) => e.stopPropagation()}>
                                <button onClick={(e) => handleEditClick(e, patient)} className="text-yellow-500 hover:text-yellow-600 bg-yellow-50 p-2 rounded"><Edit size={18} /></button>
                                <button onClick={(e) => confirmDeleteClick(e, patient)} className="text-red-500 hover:text-red-600 bg-red-50 p-2 rounded"><Trash2 size={18} /></button>
                            </td>
                        </tr>
                    );
                })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination (Hidden for brevity, same as before) */}
      {entriesPerPage !== 'all' && totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-4">
          <button onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1} className="px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1"><ChevronLeft size={16} /> Previous</button>
          <div className="flex gap-1">{[...Array(totalPages)].map((_, idx) => { const pageNum = idx + 1; if (pageNum === 1 || pageNum === totalPages || (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)) { return (<button key={pageNum} onClick={() => setCurrentPage(pageNum)} className={`px-3 py-1.5 border rounded ${currentPage === pageNum ? 'bg-teal-600 text-white border-teal-600' : 'border-gray-300 hover:bg-gray-50'}`}>{pageNum}</button>); } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) { return <span key={pageNum} className="px-2">...</span>; } return null; })}</div>
          <button onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages} className="px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1">Next <ChevronRight size={16} /></button>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-1/3 p-6 relative">
                <button onClick={() => setShowModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><X size={24} /></button>
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">{isEditing ? <Edit className="text-yellow-500"/> : <Plus className="text-teal-600" />} {isEditing ? 'Edit Patient Details' : 'New Patient Registration'}</h3>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div><label className="block text-sm font-medium text-gray-700">Full Name</label><input type="text" name="full_name" required className="mt-1 block w-full border border-gray-300 rounded-md p-2" value={formData.full_name} onChange={handleInputChange}/></div>
                        
                        {/* ✅ UPDATED STATUS DROPDOWN */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Status</label>
                            <select name="status" className="mt-1 block w-full border border-gray-300 rounded-md p-2 bg-white" value={formData.status} onChange={handleInputChange}>
                                <option value="Active">Active</option>
                                <option value="Inactive">Inactive</option>
                                <option value="Hold">Hold</option>
                                <option value="Deceased">Deceased</option>
                            </select>
                        </div>
                    </div>
                    <div><label className="block text-sm font-medium text-gray-700">Home Address</label><input type="text" name="address" placeholder="Street, City, Zip" className="mt-1 block w-full border border-gray-300 rounded-md p-2" value={formData.address} onChange={handleInputChange}/></div>
                    <div className="grid grid-cols-2 gap-4"><div><label className="block text-sm font-medium text-gray-700">Phone Number</label><input type="text" name="phone_number" required className="mt-1 block w-full border border-gray-300 rounded-md p-2" value={formData.phone_number} onChange={handleInputChange}/></div><div><label className="block text-sm font-medium text-gray-700">Date of Birth</label><input type="date" name="dob" required className="mt-1 block w-full border border-gray-300 rounded-md p-2" value={formData.dob} onChange={handleInputChange}/></div></div>
                    <div><label className="block text-sm font-medium text-gray-700">Insurance Provider</label><input type="text" name="insurance_provider" placeholder="e.g. BlueCross" className="mt-1 block w-full border border-gray-300 rounded-md p-2" value={formData.insurance_provider} onChange={handleInputChange}/></div>
                    
                    <button type="submit" className={`w-full text-white font-bold py-2 rounded transition mt-4 ${isEditing ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-teal-600 hover:bg-teal-700'}`}>{isEditing ? 'Update Patient' : 'Register Patient'}</button>
                </form>
            </div>
        </div>
      )}

      {showDeleteModal && patientToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-96 p-6 relative text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4"><AlertTriangle className="h-6 w-6 text-red-600" /></div>
                <h3 className="text-lg font-bold text-gray-900">Confirm Deletion</h3>
                <p className="text-sm text-gray-500 mt-2">Are you sure you want to delete <strong>{patientToDelete.full_name}</strong>?</p>
                <div className="mt-6 flex justify-center gap-3"><button onClick={() => setShowDeleteModal(false)} className="bg-white text-gray-700 border border-gray-300 px-4 py-2 rounded font-medium hover:bg-gray-50">Cancel</button><button onClick={executeDelete} className="bg-red-600 text-white px-4 py-2 rounded font-medium hover:bg-red-700">Delete</button></div>
            </div>
        </div>
      )}
    </div>
  );
};

export default Patients;